import { useOptionsSymbolSearchService } from '../hooks/useSymbolSearchService';
import { useValidDates } from '../hooks/useValidDates';
import { useValidPrices } from '../hooks/useValidPrices';
import { setExpirationDate, setOptionCallPut, setStrikePrice, setDropdownItems } from '../state';
import type { FocusedElement, SymbolPickerState } from '../state';
import type { OptionCallPut } from '../types';
import { CopySymbol } from './CopySymbol';
import { OptionsCallPutPicker } from './OptionsCallPutPicker';
import { OptionsDatePicker } from './OptionsDatePicker';
import { OptionsPricePicker } from './OptionsPricePicker';
import { SymbolInput } from './SymbolInput';
import { useSignals } from '@preact/signals-react/runtime';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useEvent } from 'src/hooks/useEvent';
import { getSymbolSearchServiceQuery, selectExactMatches } from 'src/queries/symbolSearchService';
import type { RecentlyUsedSymbolsService } from 'src/services/RecentlyUsedSymbolsService';

export interface OptionsProps {
    state: SymbolPickerState;
    onUnderlyingSymbolChange: (symbol: string) => void;
    onUnderlyingSymbolClear: () => void;
    dispatch: (action: any) => void;
    onDropdownSearchForElement: (element: FocusedElement, term: string) => void;
    onDropdownItems: (element: FocusedElement, items: any[]) => void;
    onDropdownActiveIndex: (index: number) => void;
    onFocus: (element: FocusedElement) => void;
    clearable?: boolean;
    recentlyUsedSymbolsService: RecentlyUsedSymbolsService;
    copyable?: boolean;
}

export function Options({
    state,
    dispatch,
    onUnderlyingSymbolChange,
    onUnderlyingSymbolClear,
    onDropdownSearchForElement,
    onDropdownItems,
    onDropdownActiveIndex,
    onFocus,
    clearable,
    recentlyUsedSymbolsService,
    copyable = true,
}: OptionsProps) {
    useSignals();

    const { symbolSearchService } = useOptionsSymbolSearchService();

    const handleExpirationDateChange = useEvent((date: string) => {
        dispatch(setExpirationDate(date, 'forward'));
    });

    const handleExpirationDateClear = useEvent(() => {
        dispatch(setExpirationDate('', 'focus'));
    });

    const handleOptionCallPutChange = useEvent((type: OptionCallPut | undefined) => {
        dispatch(setOptionCallPut(type, 'forward'));
    });

    const handleStrikePriceChange = useEvent((price: number) => {
        dispatch(setStrikePrice(price, 'forward'));
    });

    const handleStrikePriceClear = useEvent(() => {
        dispatch(setStrikePrice(undefined, 'focus'));
    });

    const handleUpdateDropdownItems = useEvent((element: FocusedElement, items: any[], loading?: boolean) => {
        dispatch(setDropdownItems(element, items, loading));
    });

    const exactSymbolMatchSelector = useMemo(
        () => selectExactMatches({ underlyingSymbol: state.instrument.underlyingSymbol }),
        [state.instrument.underlyingSymbol],
    );

    useValidDates({
        underlyingSymbol: state.instrument.underlyingSymbol,
        strikePrice: state.instrument.strikePrice,
        optionCallPut: state.instrument.optionCallPut,
        onUpdate: handleUpdateDropdownItems,
    });

    useValidPrices({
        underlyingSymbol: state.instrument.underlyingSymbol,
        expirationDate: state.instrument.expirationDate,
        optionCallPut: state.instrument.optionCallPut,
        onUpdate: handleUpdateDropdownItems,
    });

    const [isInvalidSymbol, setIsInvalidSymbol] = useState(false);

    // used when InstrumentType is options to validate if we have a "real" symbol match
    const { data: optionsSymbolMatch = [], isLoading: optionsSymbolMatchLoading } = useQuery({
        ...getSymbolSearchServiceQuery(state.instrument.underlyingSymbol, symbolSearchService),
        select: exactSymbolMatchSelector,
    });

    useEffect(() => {
        setIsInvalidSymbol(
            Boolean(state.instrument.underlyingSymbol) && optionsSymbolMatch.length === 0 && !optionsSymbolMatchLoading,
        );
    }, [optionsSymbolMatch.length, optionsSymbolMatchLoading, state.instrument.underlyingSymbol]);

    // Create wrapper functions for dropdown search handlers
    const handleUnderlyingSymbolSearch = useEvent((term: string) => {
        onDropdownSearchForElement('underlyingSymbol', term);
    });

    const handleExpirationDateSearch = useEvent((term: string) => {
        onDropdownSearchForElement('expirationDate', term);
    });

    const handleStrikePriceSearch = useEvent((term: string) => {
        onDropdownSearchForElement('strikePrice', term);
    });

    return (
        <div className="flex w-full items-center gap-0.5">
            <SymbolInput
                value={state.instrument.underlyingSymbol}
                className="min-w-0 flex-1 rounded-none"
                recentlyUsedSymbolsService={recentlyUsedSymbolsService}
                symbolSearchService={symbolSearchService}
                onSelect={onUnderlyingSymbolChange}
                onClear={onUnderlyingSymbolClear}
                isInvalidSymbol={isInvalidSymbol}
                dropdownOpen={state.openDropdown === 'underlyingSymbol'}
                dropdown={state.dropdowns.underlyingSymbol}
                onDropdownSearch={handleUnderlyingSymbolSearch}
                onDropdownItems={onDropdownItems}
                onDropdownActiveIndex={onDropdownActiveIndex}
                onFocus={onFocus}
                clearable={clearable}
            />
            <span className="text-gray-400">|</span>
            <OptionsDatePicker
                dropdownOpen={state.openDropdown === 'expirationDate'}
                value={state.instrument.expirationDate}
                onChange={handleExpirationDateChange}
                onClear={handleExpirationDateClear}
                dropdown={state.dropdowns.expirationDate}
                onDropdownSearch={handleExpirationDateSearch}
                onDropdownActiveIndex={onDropdownActiveIndex}
                onFocus={onFocus}
                clearable={clearable}
            />
            <span className="text-gray-400">|</span>
            <OptionsCallPutPicker
                dropdownOpen={state.openDropdown === 'optionCallPut'}
                value={state.instrument.optionCallPut}
                onChange={handleOptionCallPutChange}
                dropdown={state.dropdowns.optionCallPut}
                onDropdownActiveIndex={onDropdownActiveIndex}
                onFocus={onFocus}
            />
            <span className="text-gray-400">|</span>
            <OptionsPricePicker
                dropdownOpen={state.openDropdown === 'strikePrice'}
                value={state.instrument.strikePrice}
                onChange={handleStrikePriceChange}
                onClear={handleStrikePriceClear}
                dropdown={state.dropdowns.strikePrice}
                onDropdownSearch={handleStrikePriceSearch}
                onDropdownActiveIndex={onDropdownActiveIndex}
                onFocus={onFocus}
                clearable={clearable}
                underlyingSymbolPrices={state.underlyingSymbolPrices}
            />
            {copyable && <CopySymbol stateInstrument={state.instrument} className="ml-0.5" />}
        </div>
    );
}
